import mapboxgl from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';
import { useTripMap, getTripMapPadding } from 'src/utils';
 import { useIsMobile } from 'src/hooks';
import useStepMarkersLayer from './layers/step-markers';
import useRouteLayer from './layers/route';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

export default function TripMap({ 
  mapRef,
  routeRef,
  startAnimation,
  stopAnimation,
  isPublicPortal,
  steps, 
  selectedStep,
  transportMode, 
  setLoadingRoute, 
  setTotalTripKms, 
  setTotalTripTime, 
  mobileDrawerHeight,
  mobileDrawerState,
  inVisualisationView,
  isDesktopViewExpanded }) {

  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);
  const halfDrawerHeightRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const isMobile = useIsMobile();
  
  const { visualisation, animationDuration, mapStyle, lineColor, distanceUnit, pitch } = useTripMap();

  // Initialize the map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      //style: 'mapbox://styles/mapbox/streets-v12',
      center: [-85.07909, 9.84913],
      zoom: isMobile ? 0.8 : 2,
      maxZoom: 9,
      minZoom: isMobile ? 0.5 : 2,
      attributionControl: false,
      dragRotate: false,
      touchPitch: false,
      style: 'mapbox://styles/mapbox/standard-satellite',
      projection: 'mercator',
      //minZoom: 0.5,
    });

    if (!isMobile) {
      mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    }

    mapRef.current.on('load', () => {
      setIsMapLoaded(true);

      mapRef.current.on('moveend', () => {
        const center = mapRef.current.getCenter();
        const zoom = mapRef.current.getZoom();
        console.log('Lng:', center.lng, 'Lat:', center.lat, 'Zoom:', zoom);
      });
    });

    return () => {
      mapRef.current?.remove();
    };
  }, []);


  useEffect(() => {
    if (!isMapLoaded) return;

    if (visualisation === "route-progress-animation") {    

      // Clean up static route layer and remove markers
      if (mapRef.current.getLayer('route')) mapRef.current.removeLayer('route');
      if (mapRef.current.getSource('route')) mapRef.current.removeSource('route');
      if (markersRef.current) markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];

      // Start new animation with updated options
      setTimeout(() => {
        startAnimation(routeRef.current.coordinates, {
          animationDuration,
          color: lineColor,
          distanceUnit,
          pitch,
        });
      }, 500);
    }
  }, [visualisation, isMapLoaded, animationDuration, lineColor, distanceUnit, pitch]);

  useEffect(() => {
    if (!mapRef.current) return;

    const padding = getTripMapPadding(isMobile, halfDrawerHeightRef, mobileDrawerHeight, mobileDrawerState, isDesktopViewExpanded);

    if (isMobile) {
        mapRef.current.setPadding({
          top: padding.top,
          bottom: padding.bottom,
          left: padding.left,
          right: padding.right,
        });
    } else {
        mapRef.current.easeTo({
          padding: {
            top: padding.top,
            bottom: padding.bottom,
            left: padding.left,
            right: padding.right,
          },
          duration: 400,
          easing: t => t 
        });
    }

  }, [mobileDrawerHeight, mobileDrawerState, steps, isMobile, isDesktopViewExpanded]);

  useEffect(() => {
    if (!mapRef.current || !selectedStep || !selectedStep.point?.coordinates) return;

    const [lng, lat] = selectedStep.point.coordinates;

    if (lat == null || lng == null) return;

    const padding = getTripMapPadding(
      isMobile,
      halfDrawerHeightRef,
      mobileDrawerHeight,
      mobileDrawerState,
      isDesktopViewExpanded
    );

    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: 8, 
      speed: 1.2,
      curve: 1.2,
      padding,
      essential: true,
    });

  }, [selectedStep, isMobile, mobileDrawerHeight, mobileDrawerState, isDesktopViewExpanded]);

  useStepMarkersLayer({
    mapRef,
    steps,
    isMapLoaded,
    lineColor,
    markersRef,
    isPublicPortal,
  });

  useRouteLayer({
    mapRef,
    steps,
    transportMode,
    setLoadingRoute,
    setTotalTripKms,
    setTotalTripTime,
    routeRef,
    isMobile,
    mobileDrawerHeight,
    mobileDrawerState,
    isDesktopViewExpanded,
    isPublicPortal,
    isMapLoaded,
    halfDrawerHeightRef
  });

    return <div ref={mapContainerRef} style={{ width: '100%', height: '100%', borderRadius: inVisualisationView && isMobile ? 20 : 0 }} />;
  }


